import { useState, useCallback, useMemo } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Separator } from 'src/components/ui/separator';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Badge } from 'src/components/ui/badge';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  CircleNotchIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  TrashIcon,
  CreditCardIcon,
  PlusIcon,
  ClockIcon,
  ArrowsClockwiseIcon,
  StarIcon,
  InfoIcon,
  BuildingIcon,
  ShieldIcon,
  UserIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useGetIntegration } from 'src/api/rpc/hooks/integrations/useIntegrations';
import { orpc } from 'src/api/rpc/client';
import { useMutation } from '@tanstack/react-query';
import {
  IntegrationAvailability,
  IntegrationKey,
  PaymentAccount,
  BankAccountType,
  USState,
  IndustryCode,
  CitizenshipStatus,
  CheckbookOnboardingInfo,
  PaymentsIntegration,
  // Shared display label arrays
  US_STATES,
  INDUSTRY_CODES,
  CITIZENSHIP_OPTIONS,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { formatDateTime } from '@erp/shared-utils';
import { T, Var, Num, useGT } from 'gt-react';

// Initial onboarding form state
const INITIAL_ONBOARDING_FORM: OnboardingFormData = {
  business: {
    legalBusinessName: '',
    website: '',
    taxId: '',
    businessPhone: '',
    industry: '' as IndustryCode,
    incorporationMonth: '',
    incorporationDay: '',
    incorporationYear: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '' as USState,
      zip: '',
    },
  },
  principalOfficer: {
    firstName: '',
    lastName: '',
    ssn: '',
    dobMonth: '',
    dobDay: '',
    dobYear: '',
    occupation: '',
    citizenshipStatus: '' as CitizenshipStatus,
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '' as USState,
      zip: '',
    },
  },
};

// Form data interface for onboarding (with separate date fields)
interface OnboardingFormData {
  business: {
    legalBusinessName: string;
    website: string;
    taxId: string;
    businessPhone: string;
    industry: IndustryCode | '';
    incorporationMonth: string;
    incorporationDay: string;
    incorporationYear: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      state: USState | '';
      zip: string;
    };
  };
  principalOfficer: {
    firstName: string;
    lastName: string;
    ssn: string;
    dobMonth: string;
    dobDay: string;
    dobYear: string;
    occupation: string;
    citizenshipStatus: CitizenshipStatus | '';
    address: {
      line1: string;
      line2: string;
      city: string;
      state: USState | '';
      zip: string;
    };
  };
}

interface AddPaymentAccountFormData {
  name: string;
  institutionName: string;
  routingNumber: string;
  accountNumber: string;
  confirmAccountNumber: string;
  accountType: BankAccountType;
  isDefault: boolean;
}

interface VerifyMicroDepositsFormData {
  amount1: string;
  amount2: string;
}

export function PaymentsIntegrationSettings({
  availability,
}: {
  availability?: IntegrationAvailability;
}) {
  const isAvailable = availability?.available ?? true;
  const unavailableReason = availability?.reason;
  const { showToast } = useToast();
  const gt = useGT();
  const {
    data: integration,
    isLoading,
    error,
    refetch,
  } = useGetIntegration(IntegrationKey.Payments);

  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);
  const [termsLinkClicked, setTermsLinkClicked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const [selectedAccountForVerify, setSelectedAccountForVerify] =
    useState<PaymentAccount | null>(null);

  // Form states
  const [addForm, setAddForm] = useState<AddPaymentAccountFormData>({
    name: '',
    institutionName: '',
    routingNumber: '',
    accountNumber: '',
    confirmAccountNumber: '',
    accountType: 'CHECKING',
    isDefault: false,
  });
  const [verifyForm, setVerifyForm] = useState<VerifyMicroDepositsFormData>({
    amount1: '',
    amount2: '',
  });
  const [onboardingForm, setOnboardingForm] = useState<OnboardingFormData>(
    INITIAL_ONBOARDING_FORM,
  );

  // Check if this is first-time setup (no existing Checkbook user)
  const requiresOnboarding = useMemo(() => {
    if (!integration) return true;
    const paymentsIntegration = integration as PaymentsIntegration;
    return !paymentsIntegration.settings?.checkbookUser;
  }, [integration]);

  // Mutations
  const addPaymentAccount = useMutation(
    orpc.integrations.payments.addPaymentAccount.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const verifyMicroDeposits = useMutation(
    orpc.integrations.payments.verifyMicroDeposits.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const resendMicroDeposits = useMutation(
    orpc.integrations.payments.resendMicroDeposits.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const deletePaymentAccount = useMutation(
    orpc.integrations.payments.deletePaymentAccount.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const setDefaultPaymentAccount = useMutation(
    orpc.integrations.payments.setDefaultPaymentAccount.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const updateBusinessInfo = useMutation(
    orpc.integrations.payments.updateBusinessInfo.mutationOptions(),
  );
  const uploadSignature = useMutation(
    orpc.integrations.payments.uploadSignature.mutationOptions({
      onSuccess: () => {
        refetch();
        showToast({
          title: 'Signature Uploaded',
          message: 'Your signature has been uploaded to the payment processor.',
          color: 'success',
          icon: 'check',
        });
      },
    }),
  );

  // Edit business info dialog state
  const [isEditBusinessDialogOpen, setIsEditBusinessDialogOpen] =
    useState(false);
  const [isConfirmExitOpen, setIsConfirmExitOpen] = useState(false);
  const [editBusinessForm, setEditBusinessForm] = useState<OnboardingFormData>(
    INITIAL_ONBOARDING_FORM,
  );

  // Validate onboarding form
  const validateOnboardingForm = (): string | null => {
    const { business, principalOfficer } = onboardingForm;

    // Business validation
    if (!business.legalBusinessName.trim()) {
      return 'Legal business name is required';
    }
    if (!business.taxId.trim() || !/^\d{2}-?\d{7}$/.test(business.taxId)) {
      return 'Tax ID must be in format XX-XXXXXXX';
    }
    if (!business.industry) {
      return 'Industry is required';
    }
    if (
      !business.incorporationMonth ||
      !business.incorporationDay ||
      !business.incorporationYear
    ) {
      return 'Business incorporation date is required';
    }
    if (!business.address.line1.trim()) {
      return 'Business street address is required';
    }
    if (!business.address.city.trim()) {
      return 'Business city is required';
    }
    if (!business.address.state) {
      return 'Business state is required';
    }
    if (
      !business.address.zip.trim() ||
      !/^\d{5}(-\d{4})?$/.test(business.address.zip)
    ) {
      return 'Business ZIP code must be in format 12345 or 12345-6789';
    }

    // Principal officer validation
    if (!principalOfficer.firstName.trim()) {
      return 'Principal officer first name is required';
    }
    if (!principalOfficer.lastName.trim()) {
      return 'Principal officer last name is required';
    }
    if (
      !principalOfficer.ssn.trim() ||
      !/^\d{3}-?\d{2}-?\d{4}$/.test(principalOfficer.ssn)
    ) {
      return 'SSN must be 9 digits';
    }
    if (
      !principalOfficer.dobMonth ||
      !principalOfficer.dobDay ||
      !principalOfficer.dobYear
    ) {
      return 'Principal officer date of birth is required';
    }
    if (!principalOfficer.occupation.trim()) {
      return 'Principal officer occupation is required';
    }
    if (!principalOfficer.citizenshipStatus) {
      return 'Principal officer citizenship status is required';
    }
    if (!principalOfficer.address.line1.trim()) {
      return 'Principal officer street address is required';
    }
    if (!principalOfficer.address.city.trim()) {
      return 'Principal officer city is required';
    }
    if (!principalOfficer.address.state) {
      return 'Principal officer state is required';
    }
    if (
      !principalOfficer.address.zip.trim() ||
      !/^\d{5}(-\d{4})?$/.test(principalOfficer.address.zip)
    ) {
      return 'Principal officer ZIP code must be in format 12345 or 12345-6789';
    }

    return null;
  };

  // Build onboarding info for API
  const buildOnboardingInfo = (): CheckbookOnboardingInfo => {
    const { business, principalOfficer } = onboardingForm;
    return {
      business: {
        legalBusinessName: business.legalBusinessName,
        website: business.website || undefined,
        taxId: business.taxId,
        businessPhone: business.businessPhone || undefined,
        industry: business.industry as IndustryCode,
        incorporationDate: `${business.incorporationMonth}/${business.incorporationDay}/${business.incorporationYear}`,
        address: {
          line1: business.address.line1,
          line2: business.address.line2 || undefined,
          city: business.address.city,
          state: business.address.state as USState,
          zip: business.address.zip,
        },
      },
      principalOfficer: {
        firstName: principalOfficer.firstName,
        lastName: principalOfficer.lastName,
        ssn: principalOfficer.ssn,
        dateOfBirth: `${principalOfficer.dobMonth}/${principalOfficer.dobDay}/${principalOfficer.dobYear}`,
        occupation: principalOfficer.occupation,
        citizenshipStatus:
          principalOfficer.citizenshipStatus as CitizenshipStatus,
        address: {
          line1: principalOfficer.address.line1,
          line2: principalOfficer.address.line2 || undefined,
          city: principalOfficer.address.city,
          state: principalOfficer.address.state as USState,
          zip: principalOfficer.address.zip,
        },
      },
    };
  };

  // Handle add payment account
  const handleAddAccount = async () => {
    if (!addForm.name.trim()) {
      showToast({
        title: 'Validation Error',
        message: 'Please enter an account name',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    if (!addForm.institutionName.trim()) {
      showToast({
        title: 'Validation Error',
        message: 'Please enter the bank name',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    if (addForm.routingNumber.length !== 9) {
      showToast({
        title: 'Validation Error',
        message: 'Routing number must be 9 digits',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    if (addForm.accountNumber.length < 4) {
      showToast({
        title: 'Validation Error',
        message: 'Please enter a valid account number',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    if (addForm.accountNumber !== addForm.confirmAccountNumber) {
      showToast({
        title: 'Validation Error',
        message: 'Account numbers must match',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    // Validate onboarding info if this is first-time setup
    if (requiresOnboarding) {
      const onboardingError = validateOnboardingForm();
      if (onboardingError) {
        showToast({
          title: 'Validation Error',
          message: onboardingError,
          color: 'error',
          icon: 'error',
        });
        return;
      }

      if (!termsAccepted) {
        showToast({
          title: 'Validation Error',
          message:
            'You must accept the Checkbook Terms and Conditions to continue.',
          color: 'error',
          icon: 'error',
        });
        return;
      }
    }

    try {
      await addPaymentAccount.mutateAsync({
        name: addForm.name,
        institutionName: addForm.institutionName,
        routingNumber: addForm.routingNumber,
        accountNumber: addForm.accountNumber,
        accountType: addForm.accountType,
        isDefault: addForm.isDefault,
        // Include onboarding info only for first-time setup
        onboardingInfo: requiresOnboarding ? buildOnboardingInfo() : undefined,
        // Include T&C acceptance flag for first-time setup
        termsAccepted: requiresOnboarding ? termsAccepted : undefined,
      });

      showToast({
        title: 'Payment Account Added',
        message:
          'Two small deposits will be sent to your account for verification. This typically takes 1-2 business days.',
        color: 'success',
        icon: 'check',
      });

      setIsAddDialogOpen(false);
      setTermsAccepted(false);
      setAddForm({
        name: '',
        institutionName: '',
        routingNumber: '',
        accountNumber: '',
        confirmAccountNumber: '',
        accountType: 'CHECKING',
        isDefault: false,
      });
      setOnboardingForm(INITIAL_ONBOARDING_FORM);
    } catch (error) {
      showToast({
        title: 'Failed to Add Account',
        message:
          error instanceof Error
            ? error.message
            : 'Please verify your details and try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle verify micro-deposits
  const handleVerifyMicroDeposits = async () => {
    if (!selectedAccountForVerify) return;

    const amount1 = parseFloat(verifyForm.amount1);
    const amount2 = parseFloat(verifyForm.amount2);

    if (isNaN(amount1) || isNaN(amount2) || amount1 <= 0 || amount2 <= 0) {
      showToast({
        title: 'Validation Error',
        message: 'Please enter both deposit amounts',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    try {
      await verifyMicroDeposits.mutateAsync({
        paymentAccountId: selectedAccountForVerify.id,
        amount1,
        amount2,
      });

      showToast({
        title: 'Account Verified',
        message: 'Your payment account is now ready to use!',
        color: 'success',
        icon: 'check',
      });

      setIsVerifyDialogOpen(false);
      setSelectedAccountForVerify(null);
      setVerifyForm({ amount1: '', amount2: '' });
    } catch (error) {
      showToast({
        title: 'Verification Failed',
        message: 'The amounts entered do not match. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle resend micro-deposits
  const handleResendMicroDeposits = async (accountId: string) => {
    try {
      await resendMicroDeposits.mutateAsync({ paymentAccountId: accountId });

      showToast({
        title: 'Micro-Deposits Resent',
        message:
          'New deposits have been initiated. Please allow 1-2 business days.',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Failed to Resend',
        message: 'Please try again later.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle delete account
  const handleDeleteAccount = async (accountId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to remove this payment account? This action cannot be undone.',
      )
    ) {
      return;
    }

    try {
      await deletePaymentAccount.mutateAsync({ paymentAccountId: accountId });

      showToast({
        title: 'Account Removed',
        message: 'Payment account has been successfully removed.',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Removal Failed',
        message: 'Failed to remove payment account. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle set default
  const handleSetDefault = async (accountId: string) => {
    try {
      await setDefaultPaymentAccount.mutateAsync({
        paymentAccountId: accountId,
      });

      showToast({
        title: 'Default Account Updated',
        message: 'This account will now be used for payments by default.',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Update Failed',
        message: 'Failed to set default account. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Validate edit business form (SSN and DOB are optional for updates)
  const validateEditBusinessForm = (): string | null => {
    const { business, principalOfficer } = editBusinessForm;

    // Business validation
    if (!business.legalBusinessName.trim()) {
      return 'Legal business name is required';
    }
    if (!business.taxId.trim() || !/^\d{2}-?\d{7}$/.test(business.taxId)) {
      return 'Tax ID must be in format XX-XXXXXXX';
    }
    if (!business.industry) {
      return 'Industry is required';
    }
    if (
      !business.incorporationMonth ||
      !business.incorporationDay ||
      !business.incorporationYear
    ) {
      return 'Business incorporation date is required';
    }
    if (!business.address.line1.trim()) {
      return 'Business street address is required';
    }
    if (!business.address.city.trim()) {
      return 'Business city is required';
    }
    if (!business.address.state) {
      return 'Business state is required';
    }
    if (
      !business.address.zip.trim() ||
      !/^\d{5}(-\d{4})?$/.test(business.address.zip)
    ) {
      return 'Business ZIP code must be in format 12345 or 12345-6789';
    }

    // Principal officer validation (SSN and DOB optional for updates)
    if (!principalOfficer.firstName.trim()) {
      return 'Principal officer first name is required';
    }
    if (!principalOfficer.lastName.trim()) {
      return 'Principal officer last name is required';
    }
    // SSN is optional for updates, but if provided must be valid
    if (
      principalOfficer.ssn.trim() &&
      !/^\d{3}-?\d{2}-?\d{4}$/.test(principalOfficer.ssn)
    ) {
      return 'SSN must be 9 digits if provided';
    }
    if (!principalOfficer.occupation.trim()) {
      return 'Principal officer occupation is required';
    }
    if (!principalOfficer.citizenshipStatus) {
      return 'Principal officer citizenship status is required';
    }
    if (!principalOfficer.address.line1.trim()) {
      return 'Principal officer street address is required';
    }
    if (!principalOfficer.address.city.trim()) {
      return 'Principal officer city is required';
    }
    if (!principalOfficer.address.state) {
      return 'Principal officer state is required';
    }
    if (
      !principalOfficer.address.zip.trim() ||
      !/^\d{5}(-\d{4})?$/.test(principalOfficer.address.zip)
    ) {
      return 'Principal officer ZIP code must be in format 12345 or 12345-6789';
    }

    return null;
  };

  // Handle update business info
  const handleUpdateBusinessInfo = async () => {
    const error = validateEditBusinessForm();
    if (error) {
      showToast({
        title: 'Validation Error',
        message: error,
        color: 'error',
        icon: 'error',
      });
      return;
    }

    const { business, principalOfficer } = editBusinessForm;

    try {
      await updateBusinessInfo.mutateAsync({
        business: {
          legalBusinessName: business.legalBusinessName,
          website: business.website || '',
          taxId: business.taxId,
          businessPhone: business.businessPhone || undefined,
          industry: business.industry as IndustryCode,
          incorporationDate: `${business.incorporationMonth}/${business.incorporationDay}/${business.incorporationYear}`,
          address: {
            line1: business.address.line1,
            line2: business.address.line2 || undefined,
            city: business.address.city,
            state: business.address.state as USState,
            zip: business.address.zip,
          },
        },
        principalOfficer: {
          firstName: principalOfficer.firstName,
          lastName: principalOfficer.lastName,
          // Only include SSN if provided (optional for updates)
          ssn: principalOfficer.ssn.trim() || undefined,
          // Only include DOB if all parts provided
          dateOfBirth:
            principalOfficer.dobMonth &&
            principalOfficer.dobDay &&
            principalOfficer.dobYear
              ? `${principalOfficer.dobMonth}/${principalOfficer.dobDay}/${principalOfficer.dobYear}`
              : undefined,
          occupation: principalOfficer.occupation,
          citizenshipStatus:
            principalOfficer.citizenshipStatus as CitizenshipStatus,
          address: {
            line1: principalOfficer.address.line1,
            line2: principalOfficer.address.line2 || undefined,
            city: principalOfficer.address.city,
            state: principalOfficer.address.state as USState,
            zip: principalOfficer.address.zip,
          },
        },
      });

      showToast({
        title: 'Business Information Updated',
        message: 'Your business information has been successfully updated.',
        color: 'success',
        icon: 'check',
      });

      setIsEditBusinessDialogOpen(false);
    } catch (error) {
      showToast({
        title: 'Update Failed',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to update business information. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const handleOpenAddDialog = useCallback(() => {
    setAddForm({
      name: '',
      institutionName: '',
      routingNumber: '',
      accountNumber: '',
      confirmAccountNumber: '',
      accountType: 'CHECKING',
      isDefault: false,
    });
    // For first-time setup, show T&C dialog before the onboarding form
    if (requiresOnboarding) {
      setIsTermsDialogOpen(true);
    } else {
      setIsAddDialogOpen(true);
    }
  }, [requiresOnboarding]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
        <span>
          <T context="payments integration settings loading label">
            Loading integration settings...
          </T>
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <AlertContainer variant="destructive">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertDescription>
          <T context="payments integration settings load error message">
            Failed to load integration settings. Please try refreshing the page.
          </T>
        </AlertDescription>
      </AlertContainer>
    );
  }

  const accounts =
    integration?.key === IntegrationKey.Payments
      ? integration.settings.accounts
      : [];
  const hasAccounts = accounts.length > 0;
  const paymentsUnavailableFallback = gt(
    'Payments is not available right now.',
    {
      context: 'payments integration generic unavailable message',
    },
  );

  return (
    <div className="space-y-6">
      {/* Integration Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <span className="font-medium">
              <T context="payments integration status active label">
                Integration Active
              </T>
            </span>
          </div>
          <Badge
            variant="outline"
            className="bg-green-100 text-green-700 border-green-300"
          >
            {hasAccounts ? (
              accounts.length === 1 ? (
                <T context="payments integration connected accounts badge singular">
                  <Num>{accounts.length}</Num> Account Connected
                </T>
              ) : (
                <T context="payments integration connected accounts badge plural">
                  <Num>{accounts.length}</Num> Accounts Connected
                </T>
              )
            ) : (
              <T context="payments integration ready to configure badge">
                Ready to Configure
              </T>
            )}
          </Badge>
          {!isAvailable && (
            <Badge
              variant="outline"
              className="border-red-300 text-red-700 bg-red-50"
            >
              <T context="payments integration unavailable badge">
                Unavailable
              </T>
            </Badge>
          )}
        </div>
        {!isAvailable && (
          <p className="text-sm text-red-600 mt-2">
            {unavailableReason || paymentsUnavailableFallback}
          </p>
        )}
      </div>

      <Separator />

      {/* Business Information Card - Only show if Checkbook user exists */}
      {!requiresOnboarding && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BuildingIcon className="h-5 w-5" />
                  <T context="payments integration business information card title">
                    Business Information
                  </T>
                </CardTitle>
                <CardDescription>
                  <T context="payments integration business information card description">
                    Your business verification details on file with our payment
                    processor
                  </T>
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditBusinessDialogOpen(true)}
              >
                <T context="payments integration edit business info button">
                  Edit
                </T>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <AlertContainer>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                <T context="payments integration business info compliance notice">
                  This information is stored with our payment processor
                  (Checkbook) for compliance purposes. Sensitive information
                  like SSN is not stored in our system.
                </T>
              </AlertDescription>
            </AlertContainer>
            {integration?.key === IntegrationKey.Payments &&
              integration.settings.checkbookUser?.termsAcceptedAt && (
                <div className="space-y-0.5">
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <T context="payments integration terms accepted timestamp line">
                      Terms accepted on{' '}
                      <Var>
                        {formatDateTime(
                          integration.settings.checkbookUser.termsAcceptedAt,
                        )}
                      </Var>
                    </T>
                  </p>
                  {integration.settings.checkbookUser.termsAcceptedByName && (
                    <p className="text-sm text-gray-500 ml-5">
                      {integration.settings.checkbookUser
                        .termsAcceptedByEmail ? (
                        <T context="payments integration terms accepted by user with email">
                          By:{' '}
                          <Var>
                            {
                              integration.settings.checkbookUser
                                .termsAcceptedByName
                            }
                          </Var>{' '}
                          (
                          <Var>
                            {
                              integration.settings.checkbookUser
                                .termsAcceptedByEmail
                            }
                          </Var>
                          )
                        </T>
                      ) : (
                        <T context="payments integration terms accepted by user name only">
                          By:{' '}
                          <Var>
                            {
                              integration.settings.checkbookUser
                                .termsAcceptedByName
                            }
                          </Var>
                        </T>
                      )}
                    </p>
                  )}
                  {integration.settings.checkbookUser.termsAcceptedFromIp && (
                    <p className="text-sm text-gray-500 ml-5">
                      <T context="payments integration terms accepted from IP line">
                        IP:{' '}
                        <Var>
                          {
                            integration.settings.checkbookUser
                              .termsAcceptedFromIp
                          }
                        </Var>
                      </T>
                    </p>
                  )}
                </div>
              )}
            <a
              href="https://checkbook.io/company/terms-and-conditions/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              <T context="payments integration external link to Checkbook terms">
                View Checkbook Terms & Conditions
              </T>
              <ArrowSquareOutIcon className="h-3 w-3" />
            </a>

            <Separator className="my-3" />

            {/* Signature Upload */}
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <ShieldIcon className="h-4 w-4" />
                <T context="payments integration signature on file section title">
                  Signature on File
                </T>
              </p>
              {!(
                integration?.key === IntegrationKey.Payments &&
                integration.settings.checkbookUser?.signatureUploadedAt
              ) && (
                <AlertContainer className="border-yellow-300 bg-yellow-50 text-yellow-800">
                  <WarningCircleIcon className="h-4 w-4" />
                  <AlertDescription className="text-yellow-700">
                    <T context="payments integration signature required warning for large payments">
                      Payments over $2,000 will be blocked by Checkbook until a
                      signature is uploaded.
                    </T>
                  </AlertDescription>
                </AlertContainer>
              )}
              {integration?.key === IntegrationKey.Payments &&
                integration.settings.checkbookUser?.signatureUploadedAt && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <T context="payments integration signature uploaded timestamp line">
                      Signature uploaded on{' '}
                      <Var>
                        {formatDateTime(
                          integration.settings.checkbookUser
                            .signatureUploadedAt,
                        )}
                      </Var>
                    </T>
                  </p>
                )}
              <div>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  id="signature-upload"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) {
                      showToast({
                        title: gt('File Too Large', {
                          context: 'payments signature upload toast title',
                        }),
                        message: gt('Signature image must be under 2 MB.', {
                          context: 'payments signature upload toast message',
                        }),
                        color: 'error',
                        icon: 'error',
                      });
                      e.target.value = '';
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      const base64 = (reader.result as string).split(',')[1];
                      uploadSignature.mutate({ signatureBase64: base64 });
                      e.target.value = '';
                    };
                    reader.readAsDataURL(file);
                  }}
                  disabled={uploadSignature.isPending || !isAvailable}
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={uploadSignature.isPending || !isAvailable}
                  onClick={() =>
                    document.getElementById('signature-upload')?.click()
                  }
                >
                  {uploadSignature.isPending ? (
                    <>
                      <CircleNotchIcon className="h-4 w-4 mr-1 animate-spin" />
                      <T context="payments signature upload in progress button">
                        Uploading...
                      </T>
                    </>
                  ) : integration?.key === IntegrationKey.Payments &&
                    integration.settings.checkbookUser?.signatureUploadedAt ? (
                    <T context="payments signature update button label">
                      Update Signature
                    </T>
                  ) : (
                    <T context="payments signature upload button label">
                      Upload Signature
                    </T>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Accounts List */}
      {hasAccounts ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              <T context="payments integration payment accounts card title">
                Payment Accounts
              </T>
            </CardTitle>
            <CardDescription>
              <T context="payments integration payment accounts card description">
                Manage your bank accounts for vendor payments
              </T>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {accounts.map((account: PaymentAccount) => (
              <Card key={account.id} className="border-2">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className={`p-2 rounded-lg ${
                          account.checkbookStatus === 'VERIFIED'
                            ? 'bg-green-100'
                            : 'bg-yellow-100'
                        }`}
                      >
                        <CreditCardIcon
                          className={`h-5 w-5 ${
                            account.checkbookStatus === 'VERIFIED'
                              ? 'text-green-700'
                              : 'text-yellow-700'
                          }`}
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-base">
                            {account.name}
                          </h4>
                          {account.isDefault && (
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700 border-blue-200"
                            >
                              <StarIcon className="h-3 w-3 mr-1" />
                              <T context="payments default payment account badge">
                                Default
                              </T>
                            </Badge>
                          )}
                          {account.checkbookStatus === 'VERIFIED' ? (
                            <Badge
                              variant="outline"
                              className="bg-green-50 text-green-700 border-green-200"
                            >
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              <T context="payments verified account badge">
                                Verified
                              </T>
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-yellow-50 text-yellow-700 border-yellow-200"
                            >
                              <ClockIcon className="h-3 w-3 mr-1" />
                              <T context="payments pending verification account badge">
                                Pending Verification
                              </T>
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          <T context="payments account summary institution type mask">
                            <Var name="institution">
                              {account.institutionName}
                            </Var>
                            {' - '}
                            <Var name="accountType">{account.accountType}</Var>
                            {' ('}
                            ****
                            <Var name="mask">{account.accountMask}</Var>
                            {')'}
                          </T>
                        </div>
                        <div className="text-sm text-gray-500">
                          <T context="payments account added timestamp line">
                            Added on{' '}
                            <Var name="date">
                              {formatDateTime(account.createdAt)}
                            </Var>
                          </T>
                        </div>
                        {account.checkbookStatus === 'PENDING' &&
                          account.microDepositsReleasedAt && (
                            <div className="text-sm text-yellow-600">
                              <T context="payments micro-deposits sent notice with date">
                                Micro-deposits sent on{' '}
                                <Var name="date">
                                  {formatDateTime(
                                    account.microDepositsReleasedAt,
                                  )}
                                </Var>
                                . Check your bank statement for two small
                                deposits.
                              </T>
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {account.checkbookStatus === 'PENDING' && (
                        <>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => {
                              setSelectedAccountForVerify(account);
                              setIsVerifyDialogOpen(true);
                            }}
                          >
                            <T context="payments verify micro-deposits button">
                              Verify
                            </T>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleResendMicroDeposits(account.id)
                            }
                            disabled={resendMicroDeposits.isPending}
                            title={gt('Resend micro-deposits', {
                              context:
                                'payments resend micro-deposits icon button',
                            })}
                          >
                            {resendMicroDeposits.isPending ? (
                              <CircleNotchIcon className="h-4 w-4 animate-spin" />
                            ) : (
                              <ArrowsClockwiseIcon className="h-4 w-4" />
                            )}
                          </Button>
                        </>
                      )}
                      {account.checkbookStatus === 'VERIFIED' &&
                        !account.isDefault && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetDefault(account.id)}
                            disabled={setDefaultPaymentAccount.isPending}
                          >
                            <T context="payments set default account button">
                              Set Default
                            </T>
                          </Button>
                        )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAccount(account.id)}
                        disabled={deletePaymentAccount.isPending}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {deletePaymentAccount.isPending ? (
                          <CircleNotchIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      ) : (
        /* No Accounts - Initial Setup */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              <T context="payments empty state first account card title">
                Set Up Your First Payment Account
              </T>
            </CardTitle>
            <CardDescription>
              <T context="payments empty state first account card description">
                Add a bank account to start making ACH payments to vendors
              </T>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <CreditCardIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                <T context="payments empty state no accounts heading">
                  No payment accounts configured
                </T>
              </h3>
              <p className="text-gray-600 mb-3">
                <T context="payments empty state body">
                  Add your bank account details securely to enable ACH payments.
                </T>
              </p>
              {!isAvailable && (
                <p className="text-sm text-red-600 mb-4">
                  {unavailableReason ?? paymentsUnavailableFallback}
                </p>
              )}
              <Button onClick={handleOpenAddDialog} disabled={!isAvailable}>
                <PlusIcon className="h-4 w-4 mr-1" />
                <T context="payments empty state add bank account button">
                  Add Bank Account
                </T>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {hasAccounts && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              <T context="payments add bank account card title when accounts exist">
                Add Bank Account
              </T>
            </CardTitle>
            <CardDescription>
              <T context="payments add bank account card description micro-deposits">
                Enter bank account details securely. Your account will be
                verified through two small Checkbook micro-deposits before it
                can be used for payments.
              </T>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertContainer>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                <T context="payments banking details encryption notice">
                  Banking details are encrypted at rest in our system and only
                  used to register and verify your funding account with
                  Checkbook.
                </T>
              </AlertDescription>
            </AlertContainer>
            <div className="mt-4 flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium">
                  <T context="payments add new funding account callout title">
                    Add a new funding account
                  </T>
                </p>
                <p className="text-sm text-gray-500">
                  <T context="payments add new funding account callout description">
                    Enter the bank name, routing number, and account number
                    directly.
                  </T>
                </p>
              </div>
              <Button onClick={handleOpenAddDialog} disabled={!isAvailable}>
                <PlusIcon className="h-4 w-4 mr-1" />
                <T context="payments add bank account button in callout">
                  Add Bank Account
                </T>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Terms & Conditions Dialog (shown before onboarding for first-time setup) */}
      <Dialog
        open={isTermsDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsTermsDialogOpen(false);
            setTermsLinkClicked(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldIcon className="h-5 w-5" />
              <T context="payments Checkbook terms dialog title">
                Checkbook Terms &amp; Conditions
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="payments Checkbook terms dialog description">
                Please review and accept the Checkbook Terms &amp; Conditions
                before setting up your payment account.
              </T>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">
              <T context="payments Checkbook terms acceptance explanation">
                By clicking &ldquo;I Accept&rdquo;, you agree to
                Checkbook&apos;s Terms &amp; Conditions governing the use of
                their payment processing services.
              </T>
            </p>
            <a
              href="https://checkbook.io/company/terms-and-conditions/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              onClick={() => setTermsLinkClicked(true)}
            >
              <T context="payments link to read full Checkbook terms">
                Read the full Terms &amp; Conditions
              </T>
              <ArrowSquareOutIcon className="h-3 w-3" />
            </a>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsTermsDialogOpen(false)}
            >
              <T context="payments terms dialog cancel button">Cancel</T>
            </Button>
            <Button
              disabled={!termsLinkClicked}
              onClick={() => {
                setTermsAccepted(true);
                setIsTermsDialogOpen(false);
                setIsAddDialogOpen(true);
              }}
            >
              <T context="payments terms dialog accept button">I Accept</T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Account Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent
          className={
            requiresOnboarding
              ? 'sm:max-w-[900px] max-h-[90vh] overflow-y-auto'
              : 'sm:max-w-[500px]'
          }
        >
          <DialogHeader>
            <DialogTitle>
              {requiresOnboarding ? (
                <T context="payments add dialog title onboarding">
                  Set Up Payments
                </T>
              ) : (
                <T context="payments add dialog title add account only">
                  Add Bank Account
                </T>
              )}
            </DialogTitle>
            <DialogDescription>
              {requiresOnboarding ? (
                <T context="payments add dialog description onboarding compliance">
                  Complete business verification to enable ACH payments. This
                  information is required by our payment processor for
                  compliance.
                </T>
              ) : (
                <T context="payments add dialog description add account only">
                  Enter your banking details to add a payment account for ACH
                  payments.
                </T>
              )}
            </DialogDescription>
          </DialogHeader>

          <div
            className={
              requiresOnboarding
                ? 'grid grid-cols-1 md:grid-cols-2 gap-6 py-4'
                : 'space-y-4 py-4'
            }
          >
            {/* Left Column - Bank Account Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <CreditCardIcon className="h-4 w-4" />
                <T context="payments add dialog bank account details section heading">
                  Bank Account Details
                </T>
              </div>

              <div className="space-y-2">
                <Label htmlFor="institutionName">
                  <T context="payments bank name field label">Bank Name</T>
                </Label>
                <Input
                  id="institutionName"
                  placeholder={gt('e.g., Chase', {
                    context: 'payments bank name input placeholder',
                  })}
                  value={addForm.institutionName}
                  onChange={(e) =>
                    setAddForm((prev) => ({
                      ...prev,
                      institutionName: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Account Name */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  <T context="payments account name field label">
                    Account Name
                  </T>
                </Label>
                <Input
                  id="name"
                  placeholder={gt('e.g., Main Operating Account', {
                    context: 'payments account name input placeholder',
                  })}
                  value={addForm.name}
                  onChange={(e) =>
                    setAddForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>

              {/* Account Type */}
              <div className="space-y-2">
                <Label htmlFor="accountType">
                  <T context="payments account type field label">
                    Account Type
                  </T>
                </Label>
                <Select
                  value={addForm.accountType}
                  onValueChange={(value: BankAccountType) =>
                    setAddForm((prev) => ({ ...prev, accountType: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHECKING">
                      <T context="payments bank account type checking">
                        Checking
                      </T>
                    </SelectItem>
                    <SelectItem value="SAVINGS">
                      <T context="payments bank account type savings">
                        Savings
                      </T>
                    </SelectItem>
                    <SelectItem value="BUSINESS">
                      <T context="payments bank account type business">
                        Business
                      </T>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Routing Number */}
              <div className="space-y-2">
                <Label htmlFor="routingNumber">
                  <T context="payments routing number field label">
                    Routing Number
                  </T>
                </Label>
                <Input
                  id="routingNumber"
                  placeholder={gt('9-digit routing number', {
                    context: 'payments routing number input placeholder',
                  })}
                  value={addForm.routingNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                    setAddForm((prev) => ({ ...prev, routingNumber: value }));
                  }}
                  maxLength={9}
                />
              </div>

              {/* Account Number */}
              <div className="space-y-2">
                <Label htmlFor="accountNumber">
                  <T context="payments account number field label">
                    Account Number
                  </T>
                </Label>
                <Input
                  id="accountNumber"
                  placeholder={gt('Your full account number', {
                    context: 'payments account number input placeholder',
                  })}
                  value={addForm.accountNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setAddForm((prev) => ({ ...prev, accountNumber: value }));
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmAccountNumber">
                  <T context="payments confirm account number field label">
                    Confirm Account Number
                  </T>
                </Label>
                <Input
                  id="confirmAccountNumber"
                  placeholder={gt('Re-enter your account number', {
                    context: 'payments confirm account number placeholder',
                  })}
                  value={addForm.confirmAccountNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setAddForm((prev) => ({
                      ...prev,
                      confirmAccountNumber: value,
                    }));
                  }}
                />
              </div>

              {!requiresOnboarding && (
                <AlertContainer variant="default">
                  <ClockIcon className="h-4 w-4" />
                  <AlertDescription>
                    <T context="payments micro-deposit verification notice add account only">
                      After you add the account, two small deposits (under
                      $1.00) will be sent to verify it. This typically takes 1-2
                      business days, and your banking details are encrypted at
                      rest.
                    </T>
                  </AlertDescription>
                </AlertContainer>
              )}
            </div>

            {/* Right Column - Business & Principal Officer Info (only for first-time setup) */}
            {requiresOnboarding && (
              <div className="space-y-6">
                {/* Business Information Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <BuildingIcon className="h-4 w-4" />
                    <T context="payments onboarding business information section heading">
                      Business Information
                    </T>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="legalBusinessName">
                      <T context="payments legal business name field label">
                        Legal Business Name *
                      </T>
                    </Label>
                    <Input
                      id="legalBusinessName"
                      placeholder={gt('e.g., Acme Corporation, LLC', {
                        context: 'payments legal business name placeholder',
                      })}
                      value={onboardingForm.business.legalBusinessName}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          business: {
                            ...prev.business,
                            legalBusinessName: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="taxId">
                        <T context="payments tax id field label">
                          Tax ID (EIN) *
                        </T>
                      </Label>
                      <Input
                        id="taxId"
                        placeholder={gt('XX-XXXXXXX', {
                          context: 'payments EIN tax id format placeholder',
                        })}
                        value={onboardingForm.business.taxId}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^\d-]/g, '');
                          // Auto-format with dash
                          if (value.length === 2 && !value.includes('-')) {
                            value = value + '-';
                          }
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              taxId: value.slice(0, 10),
                            },
                          }));
                        }}
                        maxLength={10}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessPhone">
                        <T context="payments business phone field label">
                          Business Phone
                        </T>
                      </Label>
                      <Input
                        id="businessPhone"
                        placeholder={gt('(555) 555-5555', {
                          context: 'payments business phone placeholder',
                        })}
                        value={onboardingForm.business.businessPhone}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              businessPhone: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="website">
                        <T context="payments business website field label">
                          Website
                        </T>
                      </Label>
                      <Input
                        id="website"
                        placeholder={gt('https://example.com', {
                          context: 'payments business website placeholder',
                        })}
                        value={onboardingForm.business.website}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              website: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="industry">
                        <T context="payments industry field label">
                          Industry *
                        </T>
                      </Label>
                      <Select
                        value={onboardingForm.business.industry}
                        onValueChange={(value: IndustryCode) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: { ...prev.business, industry: value },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('Select industry', {
                              context: 'payments industry select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRY_CODES.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>
                      <T context="payments incorporation date field label">
                        Incorporation Date *
                      </T>
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={onboardingForm.business.incorporationMonth}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              incorporationMonth: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('MM', {
                              context:
                                'payments onboarding incorporation month select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem
                              key={i + 1}
                              value={String(i + 1).padStart(2, '0')}
                            >
                              {String(i + 1).padStart(2, '0')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={onboardingForm.business.incorporationDay}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              incorporationDay: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('DD', {
                              context:
                                'payments onboarding incorporation day select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => (
                            <SelectItem
                              key={i + 1}
                              value={String(i + 1).padStart(2, '0')}
                            >
                              {String(i + 1).padStart(2, '0')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={onboardingForm.business.incorporationYear}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              incorporationYear: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('YYYY', {
                              context:
                                'payments onboarding incorporation year select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 100 }, (_, i) => {
                            const year = new Date().getFullYear() - i;
                            return (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessAddress">
                      <T context="payments business address field label">
                        Business Address *
                      </T>
                    </Label>
                    <Input
                      id="businessAddress"
                      placeholder={gt('Street address', {
                        context: 'payments street address placeholder',
                      })}
                      value={onboardingForm.business.address.line1}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          business: {
                            ...prev.business,
                            address: {
                              ...prev.business.address,
                              line1: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <Input
                      placeholder={gt('Apt, suite, etc. (optional)', {
                        context: 'payments address line 2 placeholder',
                      })}
                      value={onboardingForm.business.address.line2}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          business: {
                            ...prev.business,
                            address: {
                              ...prev.business.address,
                              line2: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder={gt('City', {
                          context: 'payments city input placeholder',
                        })}
                        value={onboardingForm.business.address.city}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              address: {
                                ...prev.business.address,
                                city: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                      <Select
                        value={onboardingForm.business.address.state}
                        onValueChange={(value: USState) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              address: {
                                ...prev.business.address,
                                state: value,
                              },
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('State', {
                              context:
                                'payments onboarding business address state placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state.value} value={state.value}>
                              {state.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder={gt('ZIP', {
                          context: 'payments zip code placeholder',
                        })}
                        value={onboardingForm.business.address.zip}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^\d-]/g, '')
                            .slice(0, 10);
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              address: { ...prev.business.address, zip: value },
                            },
                          }));
                        }}
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Principal Officer Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <UserIcon className="h-4 w-4" />
                    <T context="payments onboarding principal officer section heading">
                      Principal Officer Information
                    </T>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-3 w-3 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          <T context="payments principal officer info tooltip compliance">
                            Required for regulatory compliance. This information
                            is sent directly to our payment processor and is not
                            stored in our system.
                          </T>
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <AlertContainer>
                    <ShieldIcon className="h-4 w-4" />
                    <AlertDescription>
                      <T context="payments principal officer sensitive data alert">
                        This sensitive information is transmitted directly to
                        our payment processor for identity verification and is
                        not stored in our database.
                      </T>
                    </AlertDescription>
                  </AlertContainer>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="principalFirstName">
                        <T context="payments principal officer first name label">
                          First Name *
                        </T>
                      </Label>
                      <Input
                        id="principalFirstName"
                        placeholder={gt('First name', {
                          context: 'payments principal first name placeholder',
                        })}
                        value={onboardingForm.principalOfficer.firstName}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              firstName: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="principalLastName">
                        <T context="payments principal officer last name label">
                          Last Name *
                        </T>
                      </Label>
                      <Input
                        id="principalLastName"
                        placeholder={gt('Last name', {
                          context: 'payments principal last name placeholder',
                        })}
                        value={onboardingForm.principalOfficer.lastName}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              lastName: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ssn">
                      <T context="payments principal officer SSN field label">
                        Social Security Number *
                      </T>
                    </Label>
                    <Input
                      id="ssn"
                      type="password"
                      placeholder={gt('XXX-XX-XXXX', {
                        context: 'payments SSN format placeholder',
                      })}
                      value={onboardingForm.principalOfficer.ssn}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^\d-]/g, '');
                        // Auto-format with dashes
                        if (value.length === 3 && !value.includes('-')) {
                          value = value + '-';
                        } else if (
                          value.length === 6 &&
                          value.split('-').length === 2
                        ) {
                          value = value + '-';
                        }
                        setOnboardingForm((prev) => ({
                          ...prev,
                          principalOfficer: {
                            ...prev.principalOfficer,
                            ssn: value.slice(0, 11),
                          },
                        }));
                      }}
                      maxLength={11}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      <T context="payments principal officer date of birth label">
                        Date of Birth *
                      </T>
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={onboardingForm.principalOfficer.dobMonth}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              dobMonth: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('MM', {
                              context:
                                'payments principal DOB month select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem
                              key={i + 1}
                              value={String(i + 1).padStart(2, '0')}
                            >
                              {String(i + 1).padStart(2, '0')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={onboardingForm.principalOfficer.dobDay}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              dobDay: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('DD', {
                              context:
                                'payments principal DOB day select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => (
                            <SelectItem
                              key={i + 1}
                              value={String(i + 1).padStart(2, '0')}
                            >
                              {String(i + 1).padStart(2, '0')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={onboardingForm.principalOfficer.dobYear}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              dobYear: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('YYYY', {
                              context:
                                'payments principal DOB year select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 80 }, (_, i) => {
                            const year = new Date().getFullYear() - 18 - i;
                            return (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="occupation">
                        <T context="payments principal officer occupation label">
                          Occupation *
                        </T>
                      </Label>
                      <Input
                        id="occupation"
                        placeholder={gt('e.g., CEO, Owner', {
                          context: 'payments occupation example placeholder',
                        })}
                        value={onboardingForm.principalOfficer.occupation}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              occupation: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="citizenshipStatus">
                        <T context="payments principal officer citizenship label">
                          Citizenship Status *
                        </T>
                      </Label>
                      <Select
                        value={
                          onboardingForm.principalOfficer.citizenshipStatus
                        }
                        onValueChange={(value: CitizenshipStatus) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              citizenshipStatus: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('Select status', {
                              context:
                                'payments citizenship status select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {CITIZENSHIP_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="principalAddress">
                      <T context="payments principal officer address label">
                        Principal Officer Address *
                      </T>
                    </Label>
                    <Input
                      id="principalAddress"
                      placeholder={gt('Street address', {
                        context:
                          'payments principal officer street address placeholder',
                      })}
                      value={onboardingForm.principalOfficer.address.line1}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          principalOfficer: {
                            ...prev.principalOfficer,
                            address: {
                              ...prev.principalOfficer.address,
                              line1: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <Input
                      placeholder={gt('Apt, suite, etc. (optional)', {
                        context:
                          'payments principal officer address line 2 placeholder',
                      })}
                      value={onboardingForm.principalOfficer.address.line2}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          principalOfficer: {
                            ...prev.principalOfficer,
                            address: {
                              ...prev.principalOfficer.address,
                              line2: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder={gt('City', {
                          context:
                            'payments principal officer city input placeholder',
                        })}
                        value={onboardingForm.principalOfficer.address.city}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              address: {
                                ...prev.principalOfficer.address,
                                city: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                      <Select
                        value={onboardingForm.principalOfficer.address.state}
                        onValueChange={(value: USState) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              address: {
                                ...prev.principalOfficer.address,
                                state: value,
                              },
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('State', {
                              context:
                                'payments principal officer state select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state.value} value={state.value}>
                              {state.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder={gt('ZIP', {
                          context: 'payments principal officer zip placeholder',
                        })}
                        value={onboardingForm.principalOfficer.address.zip}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^\d-]/g, '')
                            .slice(0, 10);
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              address: {
                                ...prev.principalOfficer.address,
                                zip: value,
                              },
                            },
                          }));
                        }}
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>

                <AlertContainer variant="default">
                  <ClockIcon className="h-4 w-4" />
                  <AlertDescription>
                    <T context="payments onboarding micro-deposit timeline notice">
                      After setup, two small deposits (under $1.00) will be sent
                      to verify your account. This typically takes 1-2 business
                      days.
                    </T>
                  </AlertDescription>
                </AlertContainer>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              disabled={addPaymentAccount.isPending}
            >
              <T context="payments add account dialog cancel button">Cancel</T>
            </Button>
            <Button
              onClick={handleAddAccount}
              disabled={addPaymentAccount.isPending}
            >
              {addPaymentAccount.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  {requiresOnboarding ? (
                    <T context="payments add account submitting onboarding">
                      Setting up...
                    </T>
                  ) : (
                    <T context="payments add account submitting add only">
                      Adding...
                    </T>
                  )}
                </>
              ) : requiresOnboarding ? (
                <T context="payments add account submit complete setup">
                  Complete Setup
                </T>
              ) : (
                <T context="payments add account submit add account">
                  Add Account
                </T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Micro-Deposits Dialog */}
      <Dialog open={isVerifyDialogOpen} onOpenChange={setIsVerifyDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              <T context="payments verify micro-deposits dialog title">
                Verify Micro-Deposits
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="payments verify micro-deposits dialog description">
                Enter the two small deposit amounts from your bank statement.
              </T>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount1">
                <T context="payments first micro-deposit amount label">
                  First Deposit Amount
                </T>
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <Input
                  id="amount1"
                  placeholder={gt('0.00', {
                    context: 'payments micro-deposit amount placeholder',
                  })}
                  value={verifyForm.amount1}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d.]/g, '');
                    setVerifyForm((prev) => ({ ...prev, amount1: value }));
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount2">
                <T context="payments second micro-deposit amount label">
                  Second Deposit Amount
                </T>
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <Input
                  id="amount2"
                  placeholder={gt('0.00', {
                    context: 'payments micro-deposit amount placeholder',
                  })}
                  value={verifyForm.amount2}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d.]/g, '');
                    setVerifyForm((prev) => ({ ...prev, amount2: value }));
                  }}
                />
              </div>
            </div>

            <p className="text-sm text-gray-500">
              <T context="payments verify micro-deposits hint about Checkbook">
                Look for two small deposits from &quot;
                <Var name="processor">Checkbook</Var>
                &quot; or similar in your recent transactions.
              </T>
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsVerifyDialogOpen(false);
                setSelectedAccountForVerify(null);
                setVerifyForm({ amount1: '', amount2: '' });
              }}
              disabled={verifyMicroDeposits.isPending}
            >
              <T context="payments verify micro-deposits cancel button">
                Cancel
              </T>
            </Button>
            <Button
              onClick={handleVerifyMicroDeposits}
              disabled={verifyMicroDeposits.isPending}
            >
              {verifyMicroDeposits.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  <T context="payments verify micro-deposits in progress">
                    Verifying...
                  </T>
                </>
              ) : (
                <T context="payments verify micro-deposits submit button">
                  Verify
                </T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm exit from Edit Business dialog */}
      <ConfirmDialog
        open={isConfirmExitOpen}
        onClose={() => setIsConfirmExitOpen(false)}
        onConfirm={() => {
          setIsConfirmExitOpen(false);
          setIsEditBusinessDialogOpen(false);
          setEditBusinessForm(INITIAL_ONBOARDING_FORM);
        }}
        title={
          <T context="payments edit business discard changes confirm title">
            Discard changes?
          </T>
        }
        message={gt(
          'You have unsaved changes. Are you sure you want to close this form? Your changes will be lost.',
          {
            context: 'payments edit business discard changes confirm message',
          },
        )}
        confirmText={gt('Discard', {
          context: 'payments edit business discard changes confirm button',
        })}
        dangerous
      />

      {/* Edit Business Information Dialog */}
      <Dialog
        open={isEditBusinessDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsConfirmExitOpen(true);
          } else {
            setIsEditBusinessDialogOpen(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <T context="payments edit business information dialog title">
                Edit Business Information
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="payments edit business information dialog description">
                Update your business verification details. Sensitive information
                like SSN is optional for updates.
              </T>
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Business Information Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <BuildingIcon className="h-4 w-4" />
                <T context="payments edit dialog business information section heading">
                  Business Information
                </T>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-legalBusinessName">
                  <T context="payments edit legal business name field label">
                    Legal Business Name *
                  </T>
                </Label>
                <Input
                  id="edit-legalBusinessName"
                  placeholder={gt('e.g., Acme Corporation, LLC', {
                    context: 'payments edit legal business name placeholder',
                  })}
                  value={editBusinessForm.business.legalBusinessName}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      business: {
                        ...prev.business,
                        legalBusinessName: e.target.value,
                      },
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-taxId">
                    <T context="payments edit tax id field label">
                      Tax ID (EIN) *
                    </T>
                  </Label>
                  <Input
                    id="edit-taxId"
                    placeholder={gt('XX-XXXXXXX', {
                      context: 'payments edit EIN format placeholder',
                    })}
                    value={editBusinessForm.business.taxId}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d-]/g, '');
                      if (value.length === 2 && !value.includes('-')) {
                        value = value + '-';
                      }
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          taxId: value.slice(0, 10),
                        },
                      }));
                    }}
                    maxLength={10}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-businessPhone">
                    <T context="payments edit business phone field label">
                      Business Phone
                    </T>
                  </Label>
                  <Input
                    id="edit-businessPhone"
                    placeholder={gt('(555) 555-5555', {
                      context: 'payments edit business phone placeholder',
                    })}
                    value={editBusinessForm.business.businessPhone}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          businessPhone: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-website">
                    <T context="payments edit website field label">Website</T>
                  </Label>
                  <Input
                    id="edit-website"
                    placeholder={gt('https://example.com', {
                      context: 'payments edit website placeholder',
                    })}
                    value={editBusinessForm.business.website}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: { ...prev.business, website: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-industry">
                    <T context="payments edit industry field label">
                      Industry *
                    </T>
                  </Label>
                  <Select
                    value={editBusinessForm.business.industry}
                    onValueChange={(value: IndustryCode) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: { ...prev.business, industry: value },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('Select industry', {
                          context: 'payments edit industry select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_CODES.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>
                  <T context="payments edit incorporation date field label">
                    Incorporation Date *
                  </T>
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <Select
                    value={editBusinessForm.business.incorporationMonth}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          incorporationMonth: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('MM', {
                          context:
                            'payments edit incorporation month select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editBusinessForm.business.incorporationDay}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: { ...prev.business, incorporationDay: value },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('DD', {
                          context:
                            'payments edit incorporation day select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editBusinessForm.business.incorporationYear}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          incorporationYear: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('YYYY', {
                          context:
                            'payments edit incorporation year select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 100 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-businessAddress">
                  <T context="payments edit business address field label">
                    Business Address *
                  </T>
                </Label>
                <Input
                  id="edit-businessAddress"
                  placeholder={gt('Street address', {
                    context: 'payments edit street address placeholder',
                  })}
                  value={editBusinessForm.business.address.line1}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      business: {
                        ...prev.business,
                        address: {
                          ...prev.business.address,
                          line1: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <Input
                  placeholder={gt('Apt, suite, etc. (optional)', {
                    context: 'payments edit address line 2 placeholder',
                  })}
                  value={editBusinessForm.business.address.line2}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      business: {
                        ...prev.business,
                        address: {
                          ...prev.business.address,
                          line2: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder={gt('City', {
                      context: 'payments edit city input placeholder',
                    })}
                    value={editBusinessForm.business.address.city}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          address: {
                            ...prev.business.address,
                            city: e.target.value,
                          },
                        },
                      }))
                    }
                  />
                  <Select
                    value={editBusinessForm.business.address.state}
                    onValueChange={(value: USState) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          address: { ...prev.business.address, state: value },
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('State', {
                          context:
                            'payments edit business address state select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder={gt('ZIP', {
                      context: 'payments edit zip code placeholder',
                    })}
                    value={editBusinessForm.business.address.zip}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^\d-]/g, '')
                        .slice(0, 10);
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          address: { ...prev.business.address, zip: value },
                        },
                      }));
                    }}
                    maxLength={10}
                  />
                </div>
              </div>
            </div>

            {/* Principal Officer Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <UserIcon className="h-4 w-4" />
                <T context="payments edit dialog principal officer section heading">
                  Principal Officer Information
                </T>
              </div>

              <AlertContainer>
                <ShieldIcon className="h-4 w-4" />
                <AlertDescription>
                  <T context="payments edit principal optional SSN DOB notice">
                    SSN and Date of Birth are optional for updates. Leave blank
                    to keep existing values.
                  </T>
                </AlertDescription>
              </AlertContainer>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-principalFirstName">
                    <T context="payments edit principal first name label">
                      First Name *
                    </T>
                  </Label>
                  <Input
                    id="edit-principalFirstName"
                    placeholder={gt('First name', {
                      context: 'payments edit principal first name placeholder',
                    })}
                    value={editBusinessForm.principalOfficer.firstName}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          firstName: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-principalLastName">
                    <T context="payments edit principal last name label">
                      Last Name *
                    </T>
                  </Label>
                  <Input
                    id="edit-principalLastName"
                    placeholder={gt('Last name', {
                      context: 'payments edit principal last name placeholder',
                    })}
                    value={editBusinessForm.principalOfficer.lastName}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          lastName: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-ssn">
                  <T context="payments edit principal SSN optional label">
                    Social Security Number (optional for updates)
                  </T>
                </Label>
                <Input
                  id="edit-ssn"
                  type="password"
                  placeholder={gt('Leave blank to keep existing', {
                    context: 'payments edit SSN optional placeholder',
                  })}
                  value={editBusinessForm.principalOfficer.ssn}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d-]/g, '');
                    if (value.length === 3 && !value.includes('-')) {
                      value = value + '-';
                    } else if (
                      value.length === 6 &&
                      value.split('-').length === 2
                    ) {
                      value = value + '-';
                    }
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      principalOfficer: {
                        ...prev.principalOfficer,
                        ssn: value.slice(0, 11),
                      },
                    }));
                  }}
                  maxLength={11}
                />
              </div>

              <div className="space-y-2">
                <Label>
                  <T context="payments edit principal DOB optional label">
                    Date of Birth (optional for updates)
                  </T>
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <Select
                    value={editBusinessForm.principalOfficer.dobMonth}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          dobMonth: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('MM', {
                          context:
                            'payments edit principal DOB month select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editBusinessForm.principalOfficer.dobDay}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          dobDay: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('DD', {
                          context:
                            'payments edit principal DOB day select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editBusinessForm.principalOfficer.dobYear}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          dobYear: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('YYYY', {
                          context:
                            'payments edit principal DOB year select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 80 }, (_, i) => {
                        const year = new Date().getFullYear() - 18 - i;
                        return (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-occupation">
                    <T context="payments edit principal occupation label">
                      Occupation *
                    </T>
                  </Label>
                  <Input
                    id="edit-occupation"
                    placeholder={gt('e.g., CEO, Owner', {
                      context: 'payments edit occupation example placeholder',
                    })}
                    value={editBusinessForm.principalOfficer.occupation}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          occupation: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-citizenshipStatus">
                    <T context="payments edit principal citizenship label">
                      Citizenship Status *
                    </T>
                  </Label>
                  <Select
                    value={editBusinessForm.principalOfficer.citizenshipStatus}
                    onValueChange={(value: CitizenshipStatus) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          citizenshipStatus: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('Select status', {
                          context:
                            'payments edit citizenship status select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIZENSHIP_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-principalAddress">
                  <T context="payments edit principal address label">
                    Principal Officer Address *
                  </T>
                </Label>
                <Input
                  id="edit-principalAddress"
                  placeholder={gt('Street address', {
                    context:
                      'payments edit principal street address placeholder',
                  })}
                  value={editBusinessForm.principalOfficer.address.line1}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      principalOfficer: {
                        ...prev.principalOfficer,
                        address: {
                          ...prev.principalOfficer.address,
                          line1: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <Input
                  placeholder={gt('Apt, suite, etc. (optional)', {
                    context:
                      'payments edit principal address line 2 placeholder',
                  })}
                  value={editBusinessForm.principalOfficer.address.line2}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      principalOfficer: {
                        ...prev.principalOfficer,
                        address: {
                          ...prev.principalOfficer.address,
                          line2: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder={gt('City', {
                      context: 'payments edit principal city placeholder',
                    })}
                    value={editBusinessForm.principalOfficer.address.city}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          address: {
                            ...prev.principalOfficer.address,
                            city: e.target.value,
                          },
                        },
                      }))
                    }
                  />
                  <Select
                    value={editBusinessForm.principalOfficer.address.state}
                    onValueChange={(value: USState) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          address: {
                            ...prev.principalOfficer.address,
                            state: value,
                          },
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={gt('State', {
                          context:
                            'payments edit principal state select placeholder',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder={gt('ZIP', {
                      context: 'payments edit principal zip placeholder',
                    })}
                    value={editBusinessForm.principalOfficer.address.zip}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^\d-]/g, '')
                        .slice(0, 10);
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          address: {
                            ...prev.principalOfficer.address,
                            zip: value,
                          },
                        },
                      }));
                    }}
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmExitOpen(true)}
              disabled={updateBusinessInfo.isPending}
            >
              <T context="payments edit business dialog cancel button">
                Cancel
              </T>
            </Button>
            <Button
              onClick={handleUpdateBusinessInfo}
              disabled={updateBusinessInfo.isPending}
            >
              {updateBusinessInfo.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  <T context="payments edit business saving in progress">
                    Saving...
                  </T>
                </>
              ) : (
                <T context="payments edit business save changes button">
                  Save Changes
                </T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
